import type {ReactElement} from 'react';
import {useContext, useState} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ShareInTeamRow} from '@FormMakerReact/components/share-in-team-row/share-in-team-row';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {hasPersistedForm} from '@FormMakerReact/lib/form-maker-access-library';
import {useIsFormMakerNavCompact} from '@FormMakerReact/hooks/use-is-form-maker-nav-compact';
import {selectFormMakerPublishState} from '@FormMakerReact/reducers/form-maker-publish-state-reducer';
import {useAppSelector} from '@/hooks';
import styles from '@FormMakerReact/layout/form-maker-header/form-maker-header.module.scss';

/** Hidden for an unsaved draft: it shares a URL that does not exist yet. */
export function ShareButton(): ReactElement | null {
  const {store, state} = useContext(formMakerContext);
  const {isSharedInTeam} = useAppSelector(selectFormMakerPublishState);
  const isCompact = useIsFormMakerNavCompact();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  if (!hasPersistedForm(store)) {
    return null;
  }
  const shareIcon = isSharedInTeam ? 'icon-group-add' : 'icon-lock';
  const selector = renderShareSelector(isCompact, shareIcon, state.isEditorLoading);
  if (state.isEditorLoading) {
    return selector;
  }
  return (
    <Dropdown
      id="form-maker-share-dropdown"
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popUpHasCustomWidth
      popupClassName={styles.shareDropdownPopup}
      doCloseOnInnerClick={false}
      isControlled
      open={isDropdownOpen}
      onOpen={(): void => {
        setIsDropdownOpen(true);
      }}
      onClose={(): void => {
        setIsDropdownOpen(false);
      }}
      selector={selector}
      popup={
        <ShareInTeamRow
          idEmailSignupForm={store.idEmailSignupForm}
          isSharedInTeam={isSharedInTeam}
          onUpsell={(): void => {
            setIsDropdownOpen(false);
          }}
        />
      }
    />
  );
}

function renderShareSelector(isCompact: boolean, icon: string, isLoading: boolean): ReactElement {
  if (isCompact) {
    return (
      <Icon
        icon={icon}
        shape={IconShape.SQUARE}
        type={IconType.DARK_GHOST}
        size={IconSize.SIZE_ICON_20}
        isDisabled={isLoading}
        ariaLabel={window.i18next.t('pmwjs_form_maker_share_aria')}
      />
    );
  }
  return <Button type={Type.DARK_SECONDARY} size={Size.SMALL} text={window.i18next.t('pmwjs_share')} icon={icon} iconPlacement={IconPlacement.LEFT} isLoading={isLoading} />;
}
